import { CircleAlert, Eye, EyeOff } from 'lucide-react';
import { forwardRef, useId, useState, type CSSProperties, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';
import styles from './Field.module.css';

export function FieldError({ children, id }: { children: ReactNode; id?: string }) {
  if (!children) return null;
  return (
    <span className={styles.error} id={id} role="alert">
      <CircleAlert size={15} strokeWidth={1.6} aria-hidden />
      <span>{children}</span>
    </span>
  );
}

export function FieldHint({ children }: { children: ReactNode }) {
  return <span className={styles.hint}>{children}</span>;
}

export interface FieldProps {
  label: ReactNode;
  /** Small text on the right of the label, e.g. «обязательно». */
  aside?: ReactNode;
  error?: string;
  hint?: ReactNode;
  children: (ids: { inputId: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
  /** Muted label style (student forms). */
  quietLabel?: boolean;
}

/** Label above, error below as text — never only colour (UI-kit rule). */
export function Field({ label, aside, error, hint, children, className, quietLabel }: FieldProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={inputId} className={cx(styles.label, quietLabel ? styles.labelQuiet : undefined)}>
        <span>{label}</span>
        {aside ? <span className={styles.aside}>{aside}</span> : null}
      </label>
      {children({ inputId, describedBy: error ? errorId : undefined, invalid: Boolean(error) })}
      {error ? <FieldError id={errorId}>{error}</FieldError> : hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  mono?: boolean;
  /** Field height as written in the design (44–50); the 1px borders are added on top. */
  height?: number;
}

/** The design measures fields as content-box: CSS height plus a 1px border on each side. */
const BORDERS = 2;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, mono, height = 46, className, style, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cx(styles.input, mono ? styles.mono : undefined, invalid ? styles.invalid : undefined, className)}
      style={{ height: height + BORDERS, ...style }}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

export interface PasswordInputProps extends Omit<InputProps, 'type'> {}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { invalid, height = 46, className, ...rest },
  ref,
) {
  const [shown, setShown] = useState(false);
  return (
    <span className={cx(styles.group, invalid ? styles.invalid : undefined, className)} style={{ height: height + BORDERS }}>
      <input ref={ref} type={shown ? 'text' : 'password'} className={styles.bare} aria-invalid={invalid || undefined} {...rest} />
      <button
        type="button"
        className={styles.eye}
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
        title={shown ? 'Скрыть пароль' : 'Показать пароль'}
      >
        {shown ? <EyeOff size={18} strokeWidth={1.6} /> : <Eye size={18} strokeWidth={1.6} />}
      </button>
    </span>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid, mono, className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cx(styles.textarea, mono ? styles.mono : undefined, invalid ? styles.invalid : undefined, className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

export interface InputGroupProps {
  invalid?: boolean;
  height?: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/** Bordered row holding a bare input with prefixes, suffixes or buttons. */
export function InputGroup({ invalid, height = 46, className, style, children }: InputGroupProps) {
  return (
    <div className={cx(styles.group, invalid ? styles.invalid : undefined, className)} style={{ height: height + BORDERS, ...style }}>
      {children}
    </div>
  );
}

export const BareInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(function BareInput(
  { mono, className, ...rest },
  ref,
) {
  return <input ref={ref} className={cx(styles.bare, mono ? styles.mono : undefined, className)} {...rest} />;
});

export function Addon({ children, boxed, className }: { children: ReactNode; boxed?: boolean; className?: string }) {
  return <span className={cx(styles.addon, boxed ? styles.addonBoxed : undefined, className)}>{children}</span>;
}
